// Cliente del módulo POS → `mcp_django` (`/api/v1/admin/pos`, `/api/v1/admin/locations`).
//
// Auth: JWT propagado por el proxy Django. Multi-tenant: header X-Org-Id
// desde localStorage como respaldo cuando el JWT no carga org_id.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  CancellationReason,
  ControlPanel,
  Closing,
  Location,
  LocationKind,
  SaleDay,
  SalesItem,
} from './types';

const DJANGO_PROXY = '/django-api';

export class PosApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'PosApiError';
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await authHeaders();
  if (init.body) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${DJANGO_PROXY}${path}`, { ...init, headers });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      /* respuesta sin cuerpo JSON — nos quedamos con el status */
    }
    throw new PosApiError(detail, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${DJANGO_PROXY}${path}`, { headers: await authHeaders() });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch {
      /* respuesta sin cuerpo JSON — nos quedamos con el status */
    }
    throw new PosApiError(detail, res.status);
  }
  return res.json() as Promise<T>;
}

export const posApi = {
  locations: (includeInactive = false): Promise<Location[]> =>
    get(`/api/v1/admin/locations/${includeInactive ? '?include_inactive=true' : ''}`),
  items: (): Promise<SalesItem[]> => get('/api/v1/admin/pos/items'),
  day: (locationId: string, businessDate?: string): Promise<SaleDay> => {
    const qs = new URLSearchParams({ location_id: locationId });
    if (businessDate) qs.set('business_date', businessDate);
    return get(`/api/v1/admin/pos/sales/day?${qs.toString()}`);
  },
  closings: (locationId?: string): Promise<Closing[]> => {
    const qs = new URLSearchParams();
    if (locationId) qs.set('location_id', locationId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return get(`/api/v1/admin/pos/closings${suffix}`);
  },
  cancellationReasons: (): Promise<CancellationReason[]> =>
    get('/api/v1/admin/pos/cancellation-reasons'),

  /** Panel del administrador. Requiere el scope `pos:totals` — un cajero
   *  recibe 403 aquí, igual que con el acumulado del día. */
  controlPanel: (dias = 7): Promise<ControlPanel> =>
    get(`/api/v1/admin/pos/control-panel?dias=${dias}`),

  // ── Escritura ─────────────────────────────────────────────────

  createItem: (body: { name: string; slug: string; sort_order?: number }): Promise<SalesItem> =>
    request('/api/v1/admin/pos/items', { method: 'POST', body: JSON.stringify(body) }),

  /** Fija un precio a partir de ahora. No edita el anterior: lo sucede, y el
   *  histórico de ventas conserva el que estaba vigente al cobrar. */
  setPrice: (body: { item_id: string; amount: string }): Promise<unknown> =>
    request('/api/v1/admin/pos/prices', { method: 'POST', body: JSON.stringify(body) }),

  createLocation: (body: {
    name: string;
    slug: string;
    kind: LocationKind;
    color?: string;
  }): Promise<Location> =>
    request('/api/v1/admin/locations/', { method: 'POST', body: JSON.stringify(body) }),

  /** `is_active: false` es el soft delete — no hay DELETE en el backend. */
  updateLocation: (id: string, body: Record<string, unknown>): Promise<Location> =>
    request(`/api/v1/admin/locations/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  cancelSale: (saleId: string, reasonId: string): Promise<unknown> =>
    request(`/api/v1/admin/pos/sales/${saleId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason_id: reasonId }),
    }),

  closeDay: (body: {
    location_id: string;
    business_date: string;
    counted_cash: string;
    received_by?: string;
    notes?: string;
  }): Promise<Closing> =>
    request('/api/v1/admin/pos/closings', { method: 'POST', body: JSON.stringify(body) }),
};
